import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiRefreshCw, FiCheck, FiExternalLink, FiBell, FiClock, FiCalendar, FiEdit, FiDollarSign, FiPlus, FiFileText } from 'react-icons/fi';

interface Notification {
  id: number;
  usuario_id: number;
  tipo: string;
  mensaje: string;
  url: string | null;
  leido: boolean;
  created_at: string;
  partners_source?: boolean;
  source_display?: string;
}

interface MobileNotificationOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkAsRead: (id: number) => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
}

// Helper function to parse service reminder messages
const parseServiceReminder = (mensaje: string) => {
  const reminderMatch = mensaje.match(/\[servicio-(\d+)-(\w+)\]/);
  if (reminderMatch) {
    const servicioId = reminderMatch[1];
    const evento = reminderMatch[2];
    const cleanMessage = mensaje.replace(/\[servicio-\d+-\w+\]/, '').trim();
    return { servicioId, evento, cleanMessage, isServiceReminder: true };
  }
  return { cleanMessage: mensaje, isServiceReminder: false };
};

// Helper function to get notification icon
const getNotificationIcon = (tipo: string, mensaje: string) => {
  const { isServiceReminder, evento } = parseServiceReminder(mensaje);
  
  if (isServiceReminder) {
    switch (evento) {
      case 'servicio_proximo':
        return <FiClock className="w-5 h-5" />;
      case 'servicio_mismo_dia':
        return <FiCalendar className="w-5 h-5" />;
      case 'servicio_cancelado':
        return <FiX className="w-5 h-5" />;
      case 'servicio_modificado':
        return <FiEdit className="w-5 h-5" />;
      default:
        return <FiBell className="w-5 h-5" />;
    }
  }
  
  switch (tipo) {
    case 'recordatorio_servicio':
      return <FiBell className="w-5 h-5" />;
    case 'factura_generada':
      return <FiDollarSign className="w-5 h-5" />;
    case 'nuevo_servicio':
      return <FiPlus className="w-5 h-5" />;
    default:
      return <FiFileText className="w-5 h-5" />;
  }
};

// Helper function to generate notification link
const generateNotificationLink = (notif: Notification) => {
  // Si la notificación tiene URL específica y no es null/undefined, usar esa
  if (notif.url && notif.url !== 'null' && notif.url !== 'undefined') {
    return notif.url;
  }
  
  // Extraer información del mensaje para generar URL específica
  const { isServiceReminder, servicioId } = parseServiceReminder(notif.mensaje);
  
  // Intentar extraer ID de servicio o factura del mensaje
  const serviceMatch = notif.mensaje.match(/servicio[^\d]*(\d+)/i);
  const facturaMatch = notif.mensaje.match(/factura[^\d]*(\d+)|FAC[^\d]*(\d+)/i);
  
  // Prioridad: servicio_id del parser, luego del regex, luego basado en tipo
  if (isServiceReminder && servicioId) {
    return `/servicios/${servicioId}`;
  }
  
  if (serviceMatch) {
    return `/servicios/${serviceMatch[1]}`;
  }
  
  // Manejar diferentes tipos de notificaciones
  switch (notif.tipo) {
    case 'factura_generada':
    case 'justificante_subido':
      if (facturaMatch) {
        return `/facturacion?factura=${facturaMatch[1] || facturaMatch[2]}`;
      }
      return '/facturacion';
      
    case 'nuevo_servicio':
    case 'recordatorio_servicio':
      return '/servicios';
      
    case 'partners_sync':
      return '/configuracion/partners';
      
    case 'sistema':
      return '/configuracion';
      
    default:
      return '/';
  }
};

const MobileNotificationOverlay: React.FC<MobileNotificationOverlayProps> = ({
  isOpen,
  onClose,
  notifications,
  onMarkAsRead,
  onRefresh,
  isRefreshing = false
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [touchStartY, setTouchStartY] = useState(0);
  const [, setTouchCurrentY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.overflow = originalStyle;
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchCurrentY(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentY = e.touches[0].clientY;
    setTouchCurrentY(currentY);
    
    const scrollElement = e.currentTarget.querySelector('.notification-scroll');
    const isAtTop = scrollElement?.scrollTop === 0;
    
    if (isAtTop && currentY > touchStartY) {
      const distance = Math.min(currentY - touchStartY, 100);
      setPullDistance(distance);
      setIsPulling(distance > 50);
    }
  };

  const handleTouchEnd = () => {
    if (isPulling) {
      onRefresh();
    }
    setPullDistance(0);
    setIsPulling(false);
  };

  const unreadCount = notifications.filter(n => !n.leido).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Overlay */}
      <div 
        className={`absolute inset-0 bg-white dark:bg-gray-900 transform transition-all duration-300 ease-out ${
          isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{
          transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center space-x-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Notificaciones
              </h2>
              {unreadCount > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold text-white bg-red-500 rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
              >
                <FiRefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={handleClose}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Pull to refresh indicator */}
          {pullDistance > 0 && (
            <div className="flex items-center justify-center py-2 bg-gray-50 dark:bg-gray-800 transition-colors">
              <div className={`transform transition-all duration-200 ${isPulling ? 'rotate-180 scale-110' : ''}`}>
                <FiRefreshCw className={`w-5 h-5 transition-colors ${
                  isPulling ? 'text-blue-600 animate-spin' : 'text-gray-500'
                }`} />
              </div>
              <span className={`ml-2 text-sm transition-colors ${
                isPulling ? 'text-blue-600 font-medium' : 'text-gray-500'
              }`}>
                {isPulling ? 'Suelta para actualizar' : 'Desliza para actualizar'}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div 
          className="notification-scroll flex-1 overflow-y-auto px-4 py-2"
          style={{ transform: `translateY(${pullDistance * 0.5}px)` }}
        >
          {notifications.length > 0 ? (
            (() => {
              const unreadNotifications = notifications.filter(n => !n.leido);
              const readNotifications = notifications.filter(n => n.leido);
              
              return (
                <div className="space-y-3">
                  {unreadNotifications.map((notif) => {
                    const { cleanMessage, isServiceReminder, servicioId } = parseServiceReminder(notif.mensaje);
                    const icon = getNotificationIcon(notif.tipo, notif.mensaje);
                    
                    return (
                  <div
                    key={notif.id}
                    className={`p-4 rounded-lg border transition-all ${
                      notif.leido
                        ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-70'
                        : isServiceReminder
                        ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
                        : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        <div className={`w-10 h-10 ${
                          notif.leido 
                            ? 'bg-gray-400 dark:bg-gray-600' 
                            : 'bg-gradient-to-br from-blue-500 to-blue-700'
                        } rounded-full flex items-center justify-center`}>
                          <span className="text-white">{icon}</span>
                        </div>
                        {!notif.leido && (
                          <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 -mt-2 -mr-1 ml-auto"></div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className={`text-sm font-semibold ${
                              notif.leido 
                                ? 'text-gray-600 dark:text-gray-400' 
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {isServiceReminder ? (
                                <span className="flex items-center space-x-2">
                                  <span>Recordatorio</span>
                                  {servicioId && (
                                    <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                                      #{servicioId}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                notif.tipo
                              )}
                              {Boolean(notif.partners_source) && (
                                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                                  Partners
                                </span>
                              )}
                            </p>
                            <p className={`text-sm ${
                              notif.leido 
                                ? 'text-gray-500 dark:text-gray-500' 
                                : 'text-gray-600 dark:text-gray-300'
                            } mt-1 line-clamp-2`}>
                              {cleanMessage}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                              {new Date(notif.created_at).toLocaleString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit',
                                day: '2-digit',
                                month: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-3 mt-3">
                          <Link
                            to={generateNotificationLink(notif)}
                            onClick={handleClose}
                            className={`flex items-center space-x-1 px-3 py-1.5 text-sm ${
                              notif.leido 
                                ? 'bg-gray-500 text-white hover:bg-gray-600' 
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            } rounded-md transition-colors`}
                          >
                            <FiExternalLink className="w-4 h-4" />
                            <span>Ver</span>
                          </Link>
                          {!notif.leido && (
                            <button
                              onClick={() => onMarkAsRead(notif.id)}
                              className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                              <FiCheck className="w-4 h-4" />
                              <span>Leído</span>
                            </button>
                          )}
                          {notif.leido && (
                            <span className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                              <FiCheck className="w-4 h-4" />
                              <span>Leída</span>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                    );
                  })}
                  
                  {unreadNotifications.length > 0 && readNotifications.length > 0 && (
                    <div className="flex items-center py-4">
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-600"></div>
                      <span className="px-4 text-sm text-gray-500 dark:text-gray-400">Anteriores</span>
                      <div className="flex-1 border-t border-gray-200 dark:border-gray-600"></div>
                    </div>
                  )}
                  
                  {readNotifications.map((notif) => {
                    const { cleanMessage, isServiceReminder, servicioId } = parseServiceReminder(notif.mensaje);
                    const icon = getNotificationIcon(notif.tipo, notif.mensaje);
                    
                    return (
                      <div
                        key={notif.id}
                        className={`p-4 rounded-lg border transition-all ${
                          notif.leido
                            ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-70'
                            : isServiceReminder
                            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700'
                            : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          {/* Icon */}
                          <div className="flex-shrink-0">
                            <div className={`w-10 h-10 ${
                              notif.leido 
                                ? 'bg-gray-400 dark:bg-gray-600' 
                                : 'bg-gradient-to-br from-blue-500 to-blue-700'
                            } rounded-full flex items-center justify-center`}>
                              <span className="text-white">{icon}</span>
                            </div>
                            {!notif.leido && (
                              <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white dark:border-gray-900 -mt-2 -mr-1 ml-auto"></div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className={`text-sm font-semibold ${
                                  notif.leido 
                                    ? 'text-gray-600 dark:text-gray-400' 
                                    : 'text-gray-900 dark:text-white'
                                }`}>
                                  {isServiceReminder ? (
                                    <span className="flex items-center space-x-2">
                                      <span>Recordatorio</span>
                                      {servicioId && (
                                        <span className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-700 rounded-full">
                                          #{servicioId}
                                        </span>
                                      )}
                                    </span>
                                  ) : (
                                    notif.tipo
                                  )}
                                  {Boolean(notif.partners_source) && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full">
                                      Partners
                                    </span>
                                  )}
                                </p>
                                <p className={`text-sm ${
                                  notif.leido 
                                    ? 'text-gray-500 dark:text-gray-500' 
                                    : 'text-gray-600 dark:text-gray-300'
                                } mt-1 line-clamp-2`}>
                                  {cleanMessage}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                  {new Date(notif.created_at).toLocaleString('es-ES', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    day: '2-digit',
                                    month: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center space-x-3 mt-3">
                              <Link
                                to={generateNotificationLink(notif)}
                                onClick={handleClose}
                                className={`flex items-center space-x-1 px-3 py-1.5 text-sm ${
                                  notif.leido 
                                    ? 'bg-gray-500 text-white hover:bg-gray-600' 
                                    : 'bg-blue-600 text-white hover:bg-blue-700'
                                } rounded-md transition-colors`}
                              >
                                <FiExternalLink className="w-4 h-4" />
                                <span>Ver</span>
                              </Link>
                              {!notif.leido && (
                                <button
                                  onClick={() => onMarkAsRead(notif.id)}
                                  className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                                >
                                  <FiCheck className="w-4 h-4" />
                                  <span>Leído</span>
                                </button>
                              )}
                              {notif.leido && (
                                <span className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">
                                  <FiCheck className="w-4 h-4" />
                                  <span>Leída</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <FiBell className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <p className="text-gray-500 dark:text-gray-400 text-center">
                No hay notificaciones nuevas
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 p-4">
          <Link
            to="/notifications"
            onClick={handleClose}
            className="block w-full py-3 text-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
          >
            Ver todas las notificaciones
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MobileNotificationOverlay;